import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, BookOpen, Calendar, CheckCircle2, ClipboardList,
  ExternalLink, GraduationCap, KeyRound, Mail, MessageSquare, Package, Pencil,
  Phone, Plus, UserMinus, UserRound, X, Loader2,
} from "lucide-react";
import {
  ACCESS_STATUS_LABEL, FU_TYPE_LABEL, LESSON_STATUS_LABEL, STUDENT_STATUS_LABEL,
  brDateTime, fmtDateBR, tr,
} from "@/lib/labels";
import StudentAnalyticsSummary from "@/components/analytics/StudentAnalyticsSummary";
import { EmptyState, PageHeader, PageLoading, SectionCard } from "@/components/doxa/AdminSurface";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const academicBadge = {
  ACTIVE: "doxa-badge-success", PENDING: "doxa-badge-warning", INACTIVE: "doxa-badge-neutral",
  DROPPED: "doxa-badge-neutral", COMPLETED: "doxa-badge-orange", REJECTED: "doxa-badge-critical",
};
const accessBadge = {
  ACTIVE: "doxa-badge-success", PENDING: "doxa-badge-warning", INACTIVE: "doxa-badge-neutral", BLOCKED: "doxa-badge-critical",
};
const classLabel = {
  ON_TRACK: { label: "Em dia", cls: "doxa-badge-success" },
  ATTENTION: { label: "Atenção", cls: "doxa-badge-warning" },
  CRITICAL: { label: "Crítico", cls: "doxa-badge-critical" },
};

const TAB_ITEMS = [
  ["summary", "Resumo"],
  ["data", "Dados"],
  ["lessons", "Aulas"],
  ["tasks", "Tarefas"],
  ["materials", "Materiais"],
  ["absences", "Ausências"],
  ["followups", "Acompanhamentos"],
];
const VALID_TABS = new Set(TAB_ITEMS.map(([key]) => key));
const FU_OPTIONS = ["GENERAL", "PASTORAL", "ATTENDANCE", "TASK", "BEHAVIOR", "OTHER"].map((value) => ({
  value,
  label: tr(FU_TYPE_LABEL, value),
}));

export default function StudentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canManage = can("manage.students");
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [student, setStudent] = useState(null);
  const [tab, setTab] = useState(VALID_TABS.has(requestedTab) ? requestedTab : "summary");
  const [analytics, setAnalytics] = useState(undefined);
  const [followUps, setFollowUps] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [absences, setAbsences] = useState({ summary: { total: 0, justified: 0, unjustified: 0 }, items: [] });
  const [absFilter, setAbsFilter] = useState("ALL");
  const [newFu, setNewFu] = useState("");
  const [fuType, setFuType] = useState("GENERAL");
  const [cells, setCells] = useState([]);
  const [editCell, setEditCell] = useState(false);
  const [showInactivate, setShowInactivate] = useState(false);
  const [inactivating, setInactivating] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/students/${id}`);
    setStudent(data);
    setAnalytics(undefined);
    setLessons([]);
    setTasks([]);
    setMaterials([]);

    api.get(`/analytics/students/${id}`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
    if (data.class_group_id) {
      api.get("/lessons", { params: { class_group_id: data.class_group_id } }).then((r) => setLessons(r.data));
      api.get("/tasks", { params: { class_group_id: data.class_group_id } }).then((r) => setTasks(r.data));
      api.get("/materials", { params: { class_group_id: data.class_group_id } }).then((r) => setMaterials(r.data));
    }
    api.get("/follow-ups", { params: { student_id: id } }).then((r) => setFollowUps(r.data));
    api.get(`/students/${id}/absences`).then((r) => setAbsences(r.data)).catch(() => setAbsences({ summary: { total: 0, justified: 0, unjustified: 0 }, items: [] }));
  };

  useEffect(() => { void load(); }, [id]);
  useEffect(() => {
    if (VALID_TABS.has(requestedTab)) setTab(requestedTab);
  }, [requestedTab]);
  useEffect(() => {
    if (canManage) api.get("/cells", { params: { include_inactive: true } }).then((r) => setCells(r.data));
  }, [canManage]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === "summary") next.delete("tab");
    else next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  const absItems = (absences.items || []).filter((a) =>
    absFilter === "ALL" ? true : absFilter === "JUSTIFIED" ? a.situation === "JUSTIFIED" : a.situation === "UNJUSTIFIED"
  );

  const confirmInactivate = async () => {
    setInactivating(true);
    try {
      await api.post(`/students/${id}/inactivate`);
      toast.success("Status acadêmico alterado para Inativo. Histórico preservado.");
      setShowInactivate(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInactivating(false);
    }
  };

  const addFollowUp = async () => {
    if (!newFu.trim()) return;
    try {
      await api.post("/follow-ups", { student_id: id, content: newFu, type: fuType });
      setNewFu("");
      const { data } = await api.get("/follow-ups", { params: { student_id: id } });
      setFollowUps(data);
      toast.success("Acompanhamento registrado.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!student) return <PageLoading label="Carregando aluno…" />;

  const classification = classLabel[student.classification] || classLabel.ON_TRACK;
  const headerDescription = [student.email, student.phone].filter(Boolean).join(" · ");
  const headerMeta = `${student.cell_group || "Sem célula"} · ${tr(STUDENT_STATUS_LABEL, student.status)}`;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="student-detail-page">
      <Link
        to="/students"
        className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold text-[#6B655E] transition hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar para alunos
      </Link>

      <PageHeader
        compact
        eyebrow="Jornada do aluno"
        title={student.full_name}
        description={headerDescription}
        meta={headerMeta}
        action={<span className={`doxa-badge ${classification.cls}`} data-testid="student-classification">{classification.label}</span>}
      />

      <nav
        className="flex gap-1 overflow-x-auto rounded-[16px] border border-[#E0D9CD] bg-white p-1.5 scrollbar-hide"
        aria-label="Seções do aluno"
        data-testid="student-tabs"
      >
        {TAB_ITEMS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => changeTab(key)}
            className={`shrink-0 rounded-xl px-3.5 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 sm:text-sm ${
              tab === key
                ? "bg-[#FFF0E9] text-[#D94A12]"
                : "text-[#6B655E] hover:bg-[#FAF7F1] hover:text-[#1F1F1F]"
            }`}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "summary" && (
        analytics === undefined ? (
          <div className="space-y-4 animate-pulse" data-testid="student-summary-loading">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[0, 1, 2, 3].map((n) => <div key={n} className="h-28 rounded-2xl bg-[#F0EBE3]" />)}</div>
            <div className="h-72 rounded-2xl bg-[#F0EBE3]" />
          </div>
        ) : analytics ? (
          <StudentAnalyticsSummary analytics={analytics} classification={student.classification} />
        ) : (
          <SectionCard compact>
            <EmptyState
              icon={AlertTriangle}
              title="Resumo estatístico indisponível"
              description="Não foi possível carregar o resumo agora. As demais áreas do aluno continuam disponíveis."
            />
          </SectionCard>
        )
      )}

      {tab === "data" && (
        <SectionCard
          compact
          title="Dados e status"
          description="Informações operacionais do aluno, situação acadêmica e acesso ao portal."
          icon={UserRound}
          testId="student-data-tab"
        >
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-[14px] border border-[#EBE5DB] bg-[#FFFEFC] p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Contato e identificação</p>
              <div className="mt-3 space-y-3 text-sm">
                <InfoRow icon={Mail} label="E-mail" value={student.email || "—"} />
                <InfoRow icon={Phone} label="Telefone / WhatsApp" value={student.phone || "Não informado"} />
                {user.role !== "PROFESSOR" && <InfoRow icon={Calendar} label="Nascimento" value={student.birth_date ? fmtDateBR(student.birth_date) : "Não informado"} />}
              </div>
            </div>

            <div className="rounded-[14px] border border-[#EBE5DB] bg-[#FFFEFC] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Vínculo operacional</p>
                  <p className="mt-2 text-xs text-[#6B655E]">Célula</p>
                  <p className="mt-0.5 font-semibold text-[#1F1F1F]" data-testid="student-cell-value">{student.cell_group || "Sem célula"}</p>
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setEditCell(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#E0D9CD] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#6B655E] transition hover:border-[#F75A1B]/35 hover:bg-[#FFF0E9] hover:text-[#D94A12]"
                    data-testid="edit-cell-btn"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Editar célula
                  </button>
                )}
              </div>
              {student.class_group_name && (
                <div className="mt-4 border-t border-[#EBE5DB] pt-3">
                  <p className="text-xs text-[#6B655E]">Turma atual</p>
                  <p className="mt-0.5 font-semibold text-[#1F1F1F]">{student.class_group_name}</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-[14px] border border-[#EBE5DB] bg-[#FAF7F1] p-4">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><GraduationCap className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-[#6B655E]">Status acadêmico</p>
                  <div className="mt-1.5"><span className={`doxa-badge ${academicBadge[student.status] || "doxa-badge-neutral"}`} data-testid="student-academic-status">{tr(STUDENT_STATUS_LABEL, student.status)}</span></div>
                  <p className="mt-2 text-xs leading-relaxed text-[#8A8378]">Representa a situação do aluno na formação e é independente do acesso à conta.</p>
                  {canManage && student.status === "ACTIVE" && (
                    <button
                      type="button"
                      onClick={() => setShowInactivate(true)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[#E7B7B7] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#A52626] transition hover:bg-[#FFF1F1]"
                      data-testid="inactivate-student-btn"
                    >
                      <UserMinus className="h-3.5 w-3.5" /> Inativar aluno
                    </button>
                  )}
                </div>
              </div>
            </div>

            {user.role !== "PROFESSOR" && (
              <div className="rounded-[14px] border border-[#EBE5DB] bg-[#FAF7F1] p-4">
                <div className="flex items-start gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#E6F4EA] text-[#2F855A]"><KeyRound className="h-4 w-4" /></span>
                  <div>
                    <p className="text-xs font-semibold text-[#6B655E]">Status de acesso</p>
                    <div className="mt-1.5"><span className={`doxa-badge ${accessBadge[student.access_status] || "doxa-badge-neutral"}`} data-testid="student-access-status">{tr(ACCESS_STATUS_LABEL, student.access_status)}</span></div>
                    <p className="mt-2 text-xs leading-relaxed text-[#8A8378]">Controla a permissão de login no Portal do Aluno e não muda automaticamente com o status acadêmico.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {tab === "lessons" && (
        <SectionCard compact title="Aulas da turma" description="Aulas vinculadas à turma atual do aluno." icon={BookOpen}>
          {lessons.length > 0 ? (
            <div className="divide-y divide-[#EBE5DB]">
              {lessons.map((lesson) => (
                <div key={lesson.id} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-semibold text-[#1F1F1F]">{lesson.title}</span>
                  <span className="text-xs text-[#6B655E]">{fmtDateBR(lesson.lesson_date)} · {tr(LESSON_STATUS_LABEL, lesson.status)}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={BookOpen} title="Nenhuma aula" description="Ainda não há aulas disponíveis para a turma deste aluno." compact />
          )}
        </SectionCard>
      )}

      {tab === "tasks" && (
        <SectionCard compact title="Tarefas da turma" description="Atividades cadastradas para a turma atual." icon={ClipboardList}>
          {tasks.length > 0 ? (
            <div className="grid gap-3 lg:grid-cols-2">
              {tasks.map((task) => {
                const linkedLesson = task.lesson_id ? lessons.find((lesson) => lesson.id === task.lesson_id) : null;
                return (
                  <article
                    key={task.id}
                    className="group relative overflow-hidden rounded-[16px] border border-[#E7DED2] bg-[linear-gradient(135deg,#FFFFFF_0%,#FFFCF8_100%)] transition duration-150 hover:-translate-y-0.5 hover:border-[#F75A1B]/30 hover:shadow-[0_10px_28px_rgba(68,53,35,.07)]"
                    data-testid={`student-task-${task.id}`}
                  >
                    <span className="absolute inset-y-0 left-0 w-1 bg-[#F75A1B]" aria-hidden="true" />
                    <div className="p-4 pl-5 sm:p-5 sm:pl-6">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Atividade</p>
                          <h3 className="mt-1 font-display text-base font-bold leading-snug text-[#1F1F1F]">{task.title}</h3>
                        </div>
                        <span className={`inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold ${task.due_date ? "bg-[#FFF0E9] text-[#D94A12]" : "bg-[#F3EFE7] text-[#6B655E]"}`}>
                          <Calendar className="h-3.5 w-3.5" />
                          {task.due_date ? `Entrega ${fmtDateBR(task.due_date)}` : "Sem prazo"}
                        </span>
                      </div>

                      {task.description && (
                        <p className="mt-3 max-w-2xl whitespace-pre-wrap text-sm leading-6 text-[#5F5A53]">{task.description}</p>
                      )}

                      {linkedLesson && (
                        <div className="mt-4 flex items-center gap-2 border-t border-[#EEE7DE] pt-3 text-xs text-[#8A8378]">
                          <BookOpen className="h-3.5 w-3.5 text-[#F75A1B]" />
                          <span>Aula vinculada: <strong className="font-semibold text-[#5F5A53]">{linkedLesson.title}</strong></span>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={ClipboardList} title="Nenhuma tarefa" description="Ainda não há tarefas cadastradas para esta turma." compact />
          )}
        </SectionCard>
      )}

      {tab === "materials" && (
        <SectionCard compact title="Materiais" description="Conteúdos e entregas vinculados à turma." icon={Package}>
          {materials.length > 0 ? (
            <div className="divide-y divide-[#EBE5DB]">
              {materials.map((material) => (
                <div key={material.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-[#1F1F1F]">{material.title}</p>
                    {material.material_type === "PHYSICAL" && <span className="doxa-badge doxa-badge-orange inline-flex items-center gap-1"><Package className="h-3 w-3" /> Físico</span>}
                  </div>
                  {material.link_url && (
                    <a href={material.link_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#F75A1B] hover:text-[#D94A12]">
                      Abrir material <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {material.material_type === "PHYSICAL" && canManage && <MaterialReceiptAdmin material={material} studentId={id} />}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Package} title="Nenhum material" description="Ainda não há materiais cadastrados para esta turma." compact />
          )}
        </SectionCard>
      )}

      {tab === "absences" && (
        <SectionCard
          compact
          title="Ausências"
          description="Visão consolidada das faltas e justificativas do aluno."
          icon={AlertTriangle}
          testId="student-absences-tab"
        >
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <AbsenceStat value={absences.summary?.total ?? 0} label="Total" testId="abs-summary-total" />
            <AbsenceStat value={absences.summary?.justified ?? 0} label="Justificadas" tone="warning" testId="abs-summary-justified" />
            <AbsenceStat value={absences.summary?.unjustified ?? 0} label="Não justificadas" tone="critical" testId="abs-summary-unjustified" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="abs-filters">
            {[["ALL", "Todas"], ["UNJUSTIFIED", "Não justificadas"], ["JUSTIFIED", "Justificadas"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setAbsFilter(key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  absFilter === key
                    ? "bg-[#FFF0E9] text-[#D94A12]"
                    : "border border-[#E0D9CD] bg-white text-[#6B655E] hover:border-[#F75A1B]/35 hover:text-[#D94A12]"
                }`}
                data-testid={`abs-filter-${key.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 divide-y divide-[#EBE5DB]">
            {absItems.map((absence, index) => {
              const justified = absence.situation === "JUSTIFIED";
              return (
                <div key={absence.lesson_id || index} className="py-3 first:pt-0 last:pb-0" data-testid={`abs-item-${absence.lesson_id || index}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[#1F1F1F]">{absence.lesson_title || "—"}</p>
                    <span className={`doxa-badge ${justified ? "doxa-badge-warning" : "doxa-badge-critical"}`} data-testid={`abs-situation-${absence.lesson_id || index}`}>
                      {justified ? "Justificada" : "Não justificada"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[#6B655E]">{fmtDateBR(absence.lesson_date)}{absence.module_name ? ` · ${absence.module_name}` : ""}</p>
                  {justified && absence.justification_reason && <p className="mt-1.5 text-sm text-[#1F1F1F]">{absence.justification_reason}</p>}
                  {justified && absence.justified_at && <p className="mt-1 text-xs text-[#8A8378]">Registrada em {brDateTime(absence.justified_at)}</p>}
                </div>
              );
            })}
            {absItems.length === 0 && (
              <EmptyState
                icon={AlertTriangle}
                title={(absences.items || []).length === 0 ? "Nenhuma ausência registrada" : "Nenhuma ausência neste filtro"}
                description={(absences.items || []).length === 0 ? "Não há faltas registradas para este aluno." : "Troque o filtro para visualizar os demais registros."}
                compact
              />
            )}
          </div>
        </SectionCard>
      )}

      {tab === "followups" && (
        <div className="space-y-4">
          <SectionCard
            compact
            title="Novo acompanhamento"
            description="Registre uma observação interna sobre a jornada deste aluno."
            icon={MessageSquare}
            action={(
              <DoxaSelect
                value={fuType}
                onValueChange={setFuType}
                options={FU_OPTIONS}
                className="w-[170px] min-w-[170px]"
                align="end"
                testId="fu-type"
                ariaLabel="Tipo de acompanhamento"
              />
            )}
          >
            <textarea
              value={newFu}
              onChange={(e) => setNewFu(e.target.value)}
              rows={4}
              data-testid="fu-content"
              className="w-full resize-y rounded-xl border border-[#E0D9CD] bg-white px-3 py-3 text-sm outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
              placeholder="Anote uma observação interna sobre o aluno…"
            />
            <button onClick={addFollowUp} className="doxa-btn-primary mt-3 inline-flex items-center gap-2 px-4 py-2" data-testid="fu-submit">
              <Plus className="h-4 w-4" /> Registrar acompanhamento
            </button>
          </SectionCard>

          <SectionCard compact title="Histórico de acompanhamentos" description="Registros internos em ordem de inclusão." icon={MessageSquare}>
            {followUps.length > 0 ? (
              <div className="space-y-2">
                {followUps.map((followUp) => (
                  <article key={followUp.id} className="rounded-[14px] border border-[#EBE5DB] bg-[#FFFEFC] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="doxa-badge doxa-badge-neutral">{tr(FU_TYPE_LABEL, followUp.type)}</span>
                      <span className="text-xs text-[#8A8378]">{brDateTime(followUp.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-[#1F1F1F]">{followUp.content}</p>
                    <p className="mt-1 text-xs text-[#8A8378]">por {followUp.created_by_name || "—"}</p>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState icon={MessageSquare} title="Nenhum acompanhamento" description="Ainda não há observações internas registradas para este aluno." compact />
            )}
          </SectionCard>
        </div>
      )}

      {editCell && (
        <EditCellModal student={student} cells={cells} onClose={() => setEditCell(false)} onSaved={() => { setEditCell(false); void load(); }} />
      )}

      <AlertDialog open={showInactivate} onOpenChange={(open) => { if (!inactivating && !open) setShowInactivate(false); }}>
        <AlertDialogContent data-testid="inactivate-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar status acadêmico?</AlertDialogTitle>
            <AlertDialogDescription>
              {student.full_name} passará a ter o Status Acadêmico como <strong>Inativo</strong>. Todo o histórico acadêmico — presença, notas, provas, tarefas, justificativas e acompanhamentos — será preservado. O Status de Acesso não é alterado automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inactivating} data-testid="inactivate-student-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void confirmInactivate(); }} disabled={inactivating} className="bg-[#C53030] hover:bg-[#A52626]" data-testid="inactivate-student-confirm">
              {inactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />} Inativar aluno
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#F6EFE4] text-[#6B655E]"><Icon className="h-3.5 w-3.5" /></span>
      <div className="min-w-0">
        <p className="text-[11px] text-[#8A8378]">{label}</p>
        <p className="break-words font-medium text-[#1F1F1F]">{value}</p>
      </div>
    </div>
  );
}

function AbsenceStat({ value, label, tone = "neutral", testId }) {
  const tones = {
    neutral: "bg-[#FAF7F1] border-[#EBE5DB] text-[#1F1F1F]",
    warning: "bg-[#FFF9EE] border-[#F5E3BE] text-[#B7791F]",
    critical: "bg-[#FFF4F4] border-[#F5C6C6] text-[#C53030]",
  };
  return (
    <div className={`rounded-xl border p-3 text-center sm:p-4 ${tones[tone]}`} data-testid={testId}>
      <p className="font-display text-xl font-bold sm:text-2xl">{value}</p>
      <p className="mt-0.5 text-[10px] font-medium text-[#6B655E] sm:text-xs">{label}</p>
    </div>
  );
}

function EditCellModal({ student, cells, onClose, onSaved }) {
  const NONE_VALUE = "__NONE__";
  const [cellId, setCellId] = useState(student.cell_id || NONE_VALUE);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/students/${student.id}/cell`, { cell_id: cellId === NONE_VALUE ? null : cellId });
      toast.success("Célula do aluno atualizada com sucesso.");
      onSaved();
    } catch (err) {
      toast.error("Não foi possível atualizar a célula do aluno agora.");
    } finally {
      setBusy(false);
    }
  };

  const cellOptions = [
    { value: NONE_VALUE, label: "Sem célula" },
    ...cells.map((cell) => ({ value: cell.id, label: `${cell.name}${cell.status === "INACTIVE" ? " (inativa)" : ""}` })),
  ];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(e) => e.stopPropagation()} data-testid="edit-cell-modal">
        <div className="flex items-start justify-between gap-4 border-b border-[#EBE5DB] px-5 py-5">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><Pencil className="h-4 w-4" /></span>
            <div>
              <h2 className="font-display text-lg font-bold">Editar célula</h2>
              <p className="mt-1 text-xs text-[#6B655E]">Atualize o vínculo operacional de {student.full_name}.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6F3EE]" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-5">
          <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Célula</label>
          <DoxaSelect
            value={cellId}
            onValueChange={setCellId}
            options={cellOptions}
            className="h-11 w-full"
            contentClassName="min-w-[var(--radix-select-trigger-width)]"
            testId="edit-cell-select"
            ariaLabel="Célula do aluno"
          />
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-[#EBE5DB] bg-[#FAF7F1] px-5 py-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="h-10 rounded-xl border border-[#E0D9CD] bg-white px-4 text-sm font-semibold text-[#4A4640] hover:bg-[#F6F3EE]">Cancelar</button>
          <button onClick={save} disabled={busy} className="doxa-btn-primary h-10 px-4" data-testid="edit-cell-save">
            {busy ? "Salvando…" : "Salvar célula"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialReceiptAdmin({ material, studentId }) {
  const [rec, setRec] = useState(null);
  const load = () => api.get(`/materials/${material.id}/receipts`).then(({ data }) => {
    const all = [...data.received, ...data.pending];
    setRec(all.find((item) => item.student_id === studentId) || { received: false });
  }).catch(() => {});

  useEffect(() => { void load(); }, [material.id, studentId]);

  const toggle = async (received) => {
    try {
      await api.post(`/materials/${material.id}/receipt`, { student_id: studentId, received });
      toast.success(received ? "Recebimento registrado." : "Recebimento removido.");
      void load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!rec) return null;

  return (
    <div className="mt-2 flex items-center gap-2" data-testid={`receipt-admin-${material.id}`}>
      {rec.received ? (
        <>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2F855A]"><CheckCircle2 className="h-3.5 w-3.5" /> Recebido {rec.received_at ? `em ${brDateTime(rec.received_at)}` : ""}</span>
          <button onClick={() => toggle(false)} className="text-xs text-[#6B655E] hover:underline" data-testid={`receipt-unset-${material.id}`}>Desfazer</button>
        </>
      ) : (
        <button onClick={() => toggle(true)} className="inline-flex items-center gap-1 rounded-lg bg-[#E6F4EA] px-2.5 py-1 text-xs font-semibold text-[#2F855A]" data-testid={`receipt-set-${material.id}`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Marcar como recebido
        </button>
      )}
    </div>
  );
}
